import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { Filter } from 'lucide-react'
import { useFilters, useFilteredListings } from '@/hooks'
import { ListingGrid } from '@/components/listings'
import { SearchBar, FilterSidebar, ActiveFilters, SortSelect } from '@/components/filters'
import { Button, NoResultsState, NoListingsState, Breadcrumb } from '@/components/common'
import { SEO } from '@/components/common/SEO'
import { getCategoryGroupById, getCategoryById } from '@/utils/categories'
import type { SortOption } from '@/types/filters'
import type { BreadcrumbItem } from '@/components/common/Breadcrumb'
import { AdCarousel } from '@/components/ads/AdCarousel'

const PAGE_SIZE = 24

export function BrowsePage() {
  const { filters, setFilter, clearFilters, hasActiveFilters } = useFilters()
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE)

  // Determine if we should show vehicle filters
  const showVehicleFilters = useMemo(() => {
    if (filters.group === 'automotive') return true
    if (filters.group === 'farm-bikes-vehicles') return true
    if (filters.category?.startsWith('vehicle') || filters.category?.startsWith('motorcycle')) return true
    return false
  }, [filters.group, filters.category])

  const { listings, filteredCount, totalCount, isLoading } = useFilteredListings(
    filters,
    filters.sortBy as SortOption
  )

  // Paginated display
  const paginatedListings = useMemo(() => listings.slice(0, displayCount), [listings, displayCount])
  const hasMore = displayCount < listings.length

  const loadMore = useCallback(() => {
    setDisplayCount((prev) => prev + PAGE_SIZE)
  }, [])

  // Intersection Observer for auto-loading
  const loadMoreRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = loadMoreRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, isLoading, loadMore])

  // Reset pagination when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDisplayCount(PAGE_SIZE) }, [filters.query, filters.category, filters.group, filters.location, filters.condition, filters.saleType, filters.priceMin, filters.priceMax, filters.sortBy, filters.bodyType])

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const handleSearch = (query: string) => {
    clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => {
      setFilter('query', query)
    }, 300)
  }

  const handleRemoveFilter = (key: keyof typeof filters) => {
    setFilter(key, '')
  }

  // Get page title based on filters
  const pageTitle = useMemo(() => {
    if (filters.query) return `Search: "${filters.query}"`
    if (filters.category) {
      const category = getCategoryById(filters.category)
      return category?.name || 'Browse'
    }
    if (filters.group) {
      const group = getCategoryGroupById(filters.group)
      return group?.name || 'Browse'
    }
    return 'Browse Listings'
  }, [filters.query, filters.category, filters.group])

  // Build breadcrumb items
  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbItem[] = [{ label: 'Browse', href: '/browse' }]

    if (filters.group) {
      const group = getCategoryGroupById(filters.group)
      if (group) {
        items.push({ label: group.name, href: `/browse?group=${filters.group}` })
      }
    }

    if (filters.category) {
      const category = getCategoryById(filters.category)
      if (category) {
        items.push({ label: category.name })
      }
    }

    if (filters.query) {
      items.push({ label: `"${filters.query}"` })
    }

    return items
  }, [filters.group, filters.category, filters.query])

  // SEO description
  const seoDescription = useMemo(() => {
    if (filters.query) return `Search results for "${filters.query}" on iSQROLL — NZ Marketplace`
    if (filters.category) {
      const cat = getCategoryById(filters.category)
      return cat ? `Browse ${cat.name} listings on iSQROLL` : undefined
    }
    if (filters.group) {
      const grp = getCategoryGroupById(filters.group)
      return grp ? `Browse ${grp.name} listings on iSQROLL` : undefined
    }
    return 'Browse thousands of listings across New Zealand on iSQROLL'
  }, [filters.query, filters.category, filters.group])

  return (
    <div className="pb-24 md:pb-8">
      <SEO title={pageTitle} description={seoDescription} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://isqroll.co.nz" },
            { "@type": "ListItem", "position": 2, "name": "Browse", "item": "https://isqroll.co.nz/browse" }
          ]
        })}</script>
      </Helmet>

      {/* Breadcrumb — desktop only (saves vertical space on mobile) */}
      <div className="hidden md:block bg-white border-b border-slate-200">
        <div className="container py-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="container py-2 md:py-4">
          {/* Desktop layout */}
          <div className="hidden md:flex md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
              <p className="text-sm text-slate-500">
                {isLoading ? 'Loading...' : `${filteredCount} of ${totalCount} listings`}
              </p>
            </div>
            <SortSelect
              value={filters.sortBy as SortOption}
              onChange={(value) => setFilter('sortBy', value)}
              showRelevance={!!filters.query}
            />
          </div>
          <div className="hidden md:block mt-4">
            <SearchBar
              initialValue={filters.query}
              onSearch={handleSearch}
              placeholder="Search in results..."
            />
          </div>

          {/* Mobile layout — compact: title row + (search + filter + sort) row */}
          <div className="md:hidden space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <h1 className="text-base font-bold text-slate-900 truncate">{pageTitle}</h1>
              <span className="text-xs text-slate-500 flex-shrink-0">
                {isLoading ? '…' : `${filteredCount} of ${totalCount}`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <SearchBar
                  initialValue={filters.query}
                  onSearch={handleSearch}
                  placeholder="Search…"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowMobileFilters(true)}
                className="relative flex-shrink-0 p-2 rounded-lg border border-slate-200 bg-white text-slate-700 active:bg-slate-50"
                aria-label="Open filters"
              >
                <Filter className="w-5 h-5" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">!</span>
                )}
              </button>
              <div className="flex-shrink-0">
                <SortSelect
                  value={filters.sortBy as SortOption}
                  onChange={(value) => setFilter('sortBy', value)}
                  showRelevance={!!filters.query}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container py-6">
        {/* Active filters */}
        {hasActiveFilters && (
          <ActiveFilters
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={clearFilters}
          />
        )}

        <div className="flex gap-6">
          {/* Desktop sidebar */}
          <div className="hidden md:block">
            <FilterSidebar
              filters={filters}
              onFilterChange={setFilter}
              onClearFilters={clearFilters}
              showVehicleFilters={showVehicleFilters}
            />
          </div>

          {/* Listings grid */}
          <div className="flex-1">
            <ListingGrid
              listings={paginatedListings}
              isLoading={isLoading}
              emptyState={
                hasActiveFilters ? (
                  <NoResultsState onClearFilters={clearFilters} />
                ) : (
                  <NoListingsState />
                )
              }
            />

            {/* Inline ad between listing pages */}
            {!isLoading && paginatedListings.length >= PAGE_SIZE && (
              <div className="my-8">
                <AdCarousel placement="browse" />
              </div>
            )}

            {/* Load more sentinel + fallback button */}
            {hasMore && !isLoading && (
              <div ref={loadMoreRef} className="text-center mt-8">
                <Button variant="secondary" onClick={loadMore}>
                  Load More ({listings.length - displayCount} remaining)
                </Button>
              </div>
            )}

            {/* Results count */}
            {!isLoading && paginatedListings.length > 0 && (
              <p className="text-center text-sm text-slate-500 mt-4">
                Showing {paginatedListings.length} of {filteredCount} listings
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="font-semibold text-lg">Filters</h2>
              <Button variant="ghost" onClick={() => setShowMobileFilters(false)}>
                Done
              </Button>
            </div>
            <div className="p-4">
              <FilterSidebar
                filters={filters}
                onFilterChange={(key, value) => {
                  setFilter(key, value)
                }}
                onClearFilters={() => {
                  clearFilters()
                  setShowMobileFilters(false)
                }}
                showVehicleFilters={showVehicleFilters}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
